"use client";

import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface TruncatedTitleProps {
  title: string;
  className?: string;
}

function getTooltipText(el: HTMLSpanElement, title: string): string | null {
  const style = window.getComputedStyle(el);
  const ctx = document.createElement("canvas").getContext("2d");
  if (!ctx) return null;
  ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;

  const ellipsisWidth = ctx.measureText("…").width;
  const availableWidth = el.clientWidth - ellipsisWidth;

  let low = 0;
  let high = title.length;
  while (low < high) {
    const mid = Math.floor((low + high + 1) / 2);
    if (ctx.measureText(title.substring(0, mid)).width <= availableWidth) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }

  const spaceIndex = title.lastIndexOf(" ", low - 1);
  const wordStart = spaceIndex === -1 ? 0 : spaceIndex + 1;

  // wordStart < low  → word is cut mid-way → show full word
  // wordStart === low → clean word break → show first hidden word
  // Either way, substring(wordStart) is correct
  return "..." + title.substring(wordStart);
}

export const TruncatedTitle = ({ title, className }: TruncatedTitleProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [tooltipText, setTooltipText] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const check = () => {
      if (el.scrollWidth > el.clientWidth) {
        setTooltipText(getTooltipText(el, title));
      } else {
        setTooltipText(null);
      }
    };

    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [title]);

  useEffect(() => {
    if (!tooltipPos) return;
    const hide = () => setTooltipPos(null);
    window.addEventListener("scroll", hide, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", hide, { capture: true });
  }, [tooltipPos]);

  const handleMouseEnter = () => {
    if (!tooltipText || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setTooltipPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  };

  const handleMouseLeave = () => setTooltipPos(null);

  return (
    <>
      <span
        ref={ref}
        className={cn("truncate", className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {title}
      </span>
      {tooltipPos &&
        tooltipText &&
        createPortal(
          <div
            style={{ top: tooltipPos.top, right: tooltipPos.right }}
            className="fixed z-[9999] whitespace-nowrap rounded border border-zinc-200 bg-white px-2 py-1 text-xs text-zinc-700 shadow-md"
          >
            {tooltipText}
          </div>,
          document.body
        )}
    </>
  );
};
